import { z } from 'zod'
import { optionalText, pageQuerySchema, versionSchema } from './common.js'
import type { HealthLevel, SignalCode, SignalParam, SignalSeverity } from './project-insights.js'
import type { ProjectUserRef } from './projects.js'
import type { ProjectRefLite } from './projects-costs.js'

/**
 * Projects release P7: weekly status reports (docs/refactor/projects/05-insights-and-ai.md §7.7,
 * P7_BREAKDOWN.md). One report per project and snapshot week (Sunday to Saturday). The manager writes the
 * narrative; the key figures are copied by the server from the week's snapshot (or the live figures while
 * the week has none) and never typed. FINAL is immutable. The AI draft (P8) fills `aiAssisted`.
 */

const sunday = z.iso
  .date()
  .refine((d) => new Date(`${d}T00:00:00Z`).getUTCDay() === 0, 'A week starts on a Sunday')
const section = optionalText(4000)

export const STATUS_REPORT_STATUSES = ['DRAFT', 'FINAL'] as const
export type StatusReportStatus = (typeof STATUS_REPORT_STATUSES)[number]

/** `POST /projects/:id/status-reports` (projects.manage, or the project's manager): a DRAFT for the week. */
export const statusReportInputSchema = z.object({
  weekStart: sunday,
  /**
   * P8: the assistant run that drafted the sections (`projects.status_draft`), the user's own and
   * completed; the report is then labelled AI-assisted.
   */
  aiRunId: z.uuid().optional(),
  summary: section,
  progress: section,
  risksIssues: section,
  nextSteps: section,
})
export type StatusReportInput = z.input<typeof statusReportInputSchema>
/** A section on PATCH: omitted keeps it, null or empty clears it (P7 decision 25). */
const sectionPatch = z
  .string()
  .trim()
  .max(4000)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional()
/** `PATCH /projects/:id/status-reports/:reportId` (DRAFT only): only the sections sent change. */
export const statusReportUpdateSchema = z.object({
  /** P8: the assistant run that drafted these sections; labels the report AI-assisted (sticky). */
  aiRunId: z.uuid().optional(),
  summary: sectionPatch,
  progress: sectionPatch,
  risksIssues: sectionPatch,
  nextSteps: sectionPatch,
  version: versionSchema,
})
export type StatusReportUpdate = z.input<typeof statusReportUpdateSchema>
/**
 * `POST …/:reportId/refresh-figures {version}` (DRAFT): copies the key figures again (the week's
 * snapshot once it exists, else live). `POST …/:reportId/finalise {version}`: FINAL, frozen with the
 * figures it shows. `DELETE …/:reportId?version=`: DRAFT only.
 */
export const statusReportVersionSchema = z.object({ version: versionSchema })

/** Copied by the server; money figures null for readers without projects.financials. */
export interface StatusReportKeyFigures {
  /** SNAPSHOT: the week's frozen figures; LIVE: as of `asOf`, before the week's snapshot existed. */
  source: 'SNAPSHOT' | 'LIVE'
  asOf: string
  health: HealthLevel
  progressPct: string | null
  plannedFinish: string | null
  forecastFinish: string | null
  slipDays: number | null
  bac: string | null
  ac: string | null
  eac: string | null
  forecastMarginPct: string | null
  billed: string | null
  collected: string | null
  outstanding: string | null
  /** The week's warning and critical signals, worst first, at most 5 (money ones dropped without financials). */
  signals: { code: SignalCode; severity: SignalSeverity; params: Record<string, SignalParam> }[]
}

export interface StatusReport {
  id: string
  project: ProjectRefLite
  weekStart: string
  status: StatusReportStatus
  summary: string | null
  progress: string | null
  risksIssues: string | null
  nextSteps: string | null
  keyFigures: StatusReportKeyFigures
  /** Drafted with the assistant (P8). */
  aiAssisted: boolean
  author: ProjectUserRef | null
  createdAt: string
  updatedAt: string
  finalisedBy: ProjectUserRef | null
  finalisedAt: string | null
  can: { edit: boolean; finalise: boolean; delete: boolean; refreshFigures: boolean }
  version: number
}

export type StatusReportListItem = Omit<
  StatusReport,
  'summary' | 'progress' | 'risksIssues' | 'nextSteps' | 'can'
> & {
  /** The summary's first 200 characters. */
  excerpt: string | null
  /** The project's manager (the weekly reading groups and filters by it). Optional for fixtures. */
  manager?: ProjectUserRef
}

export const STATUS_REPORT_SORT_FIELDS = ['weekStart', 'project', 'status'] as const
/** `GET /projects/:id/status-reports` (projects.read in scope): newest week first. */
export const statusReportsQuerySchema = pageQuerySchema.extend({
  status: z.enum(STATUS_REPORT_STATUSES).optional(),
})
export type StatusReportsQuery = z.input<typeof statusReportsQuerySchema>
/**
 * `GET /project-status-reports?week=&status=&managerId=&q=` (projects.read): the reports of the projects in
 * the reader's scope, for a director's weekly reading; `missing=true` lists ACTIVE projects without a
 * report for `week` instead (as `StatusReportMissingItem`).
 */
export const portfolioStatusReportsQuerySchema = pageQuerySchema.extend({
  week: sunday.optional(),
  status: z.enum(STATUS_REPORT_STATUSES).optional(),
  managerId: z.uuid().optional(),
  missing: z.stringbool().optional(),
})
export type PortfolioStatusReportsQuery = z.input<typeof portfolioStatusReportsQuerySchema>
export interface StatusReportMissingItem {
  project: ProjectRefLite
  manager: ProjectUserRef
  /** The project's last report, if any. */
  lastWeekStart: string | null
}

export const STATUS_REPORT_ERROR_CODES = [
  /** 409: the project already has a report for that week. */
  'STATUS_REPORT_EXISTS',
  /** 422: a FINAL report is not edited, refreshed or deleted. */
  'STATUS_REPORT_FINAL',
  /** 422: a report for a week that hasn't started, or before the project's start. */
  'STATUS_REPORT_WEEK',
] as const
export type StatusReportErrorCode = (typeof STATUS_REPORT_ERROR_CODES)[number]
